import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DatabaseBackup, ExternalLink } from 'lucide-react';
import { api, ApiClientError } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.jsx';

const STATUS_TONE = {
  running: 'border-muted text-muted',
  completed: 'border-success text-success',
  failed: 'border-danger text-danger',
};

const RESTORE_TONE = {
  full: 'text-success',
  partial: 'text-warning',
  failed: 'text-danger',
};

const RESTORE_LABEL = { full: 'Restore verified', partial: 'Gzip-only check', failed: 'Restore failed' };

function formatBytes(n) {
  if (!n) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), units.length - 1);
  return `${(n / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function BackupRow({ row }) {
  return (
    <div className="flex flex-col gap-1 border-b border-border py-2.5 last:border-0 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium">{row.file_name}</p>
        <p className="text-xs text-muted">
          {new Date(row.started_at).toLocaleString('en-IN')} · {row.backup_type} · {formatBytes(row.size_bytes)}
        </p>
        {row.status === 'failed' && row.error_message ? <p className="text-xs text-danger">{row.error_message}</p> : null}
        {row.restore_status ? (
          <p className={`text-xs ${RESTORE_TONE[row.restore_status]}`}>
            {RESTORE_LABEL[row.restore_status]}
            {row.restore_note ? ` — ${row.restore_note}` : ''}
          </p>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {!row.has_local_copy ? (
          <span className="rounded-full border border-muted px-2.5 py-0.5 text-xs text-muted" title="Local copy pruned by retention — the Drive copy is the long-term archive">
            local: pruned
          </span>
        ) : null}
        <span className={`rounded-full border px-2.5 py-0.5 text-xs font-medium uppercase tracking-wide ${STATUS_TONE[row.status]}`}>
          {row.status}
        </span>
        {row.drive_link ? (
          <a href={row.drive_link} target="_blank" rel="noreferrer" className="text-muted hover:text-primary" aria-label="Open in Drive">
            <ExternalLink size={16} aria-hidden="true" />
          </a>
        ) : null}
      </div>
    </div>
  );
}

export function BackupsSection() {
  const queryClient = useQueryClient();
  const configQuery = useQuery({ queryKey: ['backups', 'config'], queryFn: () => api.get('/backups/config') });
  const listQuery = useQuery({ queryKey: ['backups', 'list'], queryFn: () => api.get('/backups') });

  const runMutation = useMutation({
    mutationFn: () => api.post('/backups/run'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['backups', 'list'] }),
  });

  const cfg = configQuery.data;

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Database backups</h2>
          <p className="mt-1 text-sm text-muted">
            {cfg
              ? `Runs automatically every day (schedule: ${cfg.cron}). Keeps the last ${cfg.keepDaily} daily and ${cfg.keepMonthly} monthly dumps on this server; each one is also queued to Google Drive for a permanent off-site copy.`
              : 'Runs automatically every night and queues a copy to Google Drive.'}
          </p>
        </div>
        <Button variant="secondary" size="sm" loading={runMutation.isPending} onClick={() => runMutation.mutate()}>
          <DatabaseBackup size={16} aria-hidden="true" /> Back up now
        </Button>
      </div>

      {runMutation.isError ? (
        <p className="text-sm text-danger">
          {runMutation.error instanceof ApiClientError ? runMutation.error.message : 'Backup failed to start.'}
        </p>
      ) : null}

      <div className="rounded-card border border-border px-3.5">
        {listQuery.isLoading ? (
          <p className="py-3 text-sm text-muted">Loading…</p>
        ) : !listQuery.data?.length ? (
          <p className="py-3 text-sm text-muted">No backups yet — the first one runs tonight, or click "Back up now".</p>
        ) : (
          listQuery.data.map((row) => <BackupRow key={row.id} row={row} />)
        )}
      </div>
    </section>
  );
}

export default BackupsSection;
